// The history ranges of the Temp and Vent tabs (one export file per range, scripts/export.sh), their x-axis
// labels in Tallinn time and the y-axis steps. No React here.
import { MONTHS, hhmm, num1 } from './format';
import { localMinuteOfDay, tallinnParts } from './tallinn_time';

const HOUR_MS = 3_600_000;

export type HistoryRange = '3h' | '24h' | '48h' | '1w' | '1m' | '3m' | '1y';

export const HISTORY_RANGES: { key: HistoryRange; label: string; name: string; hours: number }[] = [
  { key: '3h', label: '3h', name: 'Last 3 h', hours: 3 },
  { key: '24h', label: '24h', name: 'Last 24 h', hours: 24 },
  { key: '48h', label: '48h', name: 'Last 48 h', hours: 48 },
  { key: '1w', label: '1w', name: 'Last week', hours: 168 },
  { key: '1m', label: '1m', name: 'Last month', hours: 720 },
  { key: '3m', label: '3m', name: 'Last 3 months', hours: 2184 },
  { key: '1y', label: '1y', name: 'Last year', hours: 8760 },
];

export const rangeInfo = (key: HistoryRange) => HISTORY_RANGES.find((r) => r.key === key)!;

// Ranges of a month and more show dates in the readout ("Tue 13 Jan 14:20").
export const longRange = (spanMs: number) => spanMs > 168 * HOUR_MS + HOUR_MS;

// What the CO2 / level readout calls a value: a slot's own value, or an average over buckets of an hour or more.
export const isBucketed = (stepMs: number | undefined) => (stepMs ?? 0) >= HOUR_MS;

export interface XTicks {
  positions: number[];
  labels: Map<number, { text: string; bold: boolean }>;
}

// x labels for a visible span: 3h every 30 min "14:30"; 24h every 3 h; 48h every 6 h (day name in bold at
// midnight); a week: day names; a month: Mondays "5 Jan"; 3 months: month starts "Nov"; a year: every 2nd
// month. January shows the year in bold. Labels within 4 % of the edges are dropped (they would be clipped).
export function historyTicks(min: number, max: number): XTicks {
  const spanH = (max - min) / HOUR_MS;
  const labels = new Map<number, { text: string; bold: boolean }>();
  const add = (t: number, text: string, bold = false) => {
    const f = (t - min) / (max - min);
    if (f > 0.04 && f < 0.96) {
      labels.set(t, { text, bold });
    }
  };
  const everyMin = spanH <= 4.5 ? 30 : spanH <= 30 ? 180 : spanH <= 72 ? 360 : 1440;
  const step = everyMin === 30 ? HOUR_MS / 2 : HOUR_MS;
  for (let t = Math.ceil(min / step) * step; t <= max; t += step) {
    const m = localMinuteOfDay(t);
    if (m % everyMin !== 0) {
      continue;
    }
    if (everyMin < 1440) {
      if (m === 0) add(t, tallinnParts(t).weekday, true);
      else add(t, everyMin < 60 ? hhmm(t) : hhmm(t).slice(0, 2));
      continue;
    }
    // Day-based labels (m === 0: local midnight).
    const p = tallinnParts(t);
    if (spanH <= 240) {
      add(t, p.weekday);
    } else if (spanH <= 1100) {
      if (p.weekday === 'Mon') add(t, `${p.day} ${MONTHS[p.month - 1]}`);
    } else if (p.day === 1 && (spanH <= 3000 || p.month % 2 === 1)) {
      add(t, p.month === 1 ? String(p.year) : MONTHS[p.month - 1], p.month === 1);
    }
  }
  return { positions: Array.from(labels.keys()), labels };
}

// y axis from the data's extremes: a nice step for the span (at least `minStep`, and coarser while there
// would be more than `maxTicks` labels), bounds on whole steps, at least two steps tall.
const STEPS = [0.5, 1, 2, 5, 10, 20, 25, 50, 100];

export function fitAxis(
  lo: number,
  hi: number,
  minStep = 0,
  maxTicks = Infinity,
): { min: number; max: number; ticks: number[] } {
  const span = hi - lo;
  const nice = span > 30 ? 10 : span > 12 ? 5 : span > 5 ? 2 : span > 2.5 ? 1 : 0.5;
  const fit = (step: number) => {
    const min = Math.floor(lo / step) * step;
    const max = Math.max(Math.ceil(hi / step) * step, min + step * 2);
    return { min, max, step, count: Math.round((max - min) / step) + 1 };
  };
  let f = fit(Math.max(nice, minStep));
  for (const step of STEPS) {
    if (f.count <= maxTicks) {
      break;
    }
    if (step > f.step) {
      f = fit(step);
    }
  }
  const ticks: number[] = [];
  for (let v = f.min; v <= f.max + 1e-9; v += f.step) {
    ticks.push(Math.round(v * 10) / 10);
  }
  return { min: f.min, max: f.max, ticks };
}

// "−5°", "21.5°"
export const degrees = (v: number) => `${num1(v).replace(/\.0$/, '')}°`;
